import { Panel, PanelProps } from 'react-resizable-panels';

import { DRAWER_WIDTH } from 'src/constants';

const MIN_WIDTH_IN_PIXELS = 288;
const DEFAULT_WIDTH_IN_PIXELS = 396;
const DESKTOP_MAX_WIDTH_PERCENTAGE = 55;
const MOBILE_MAX_WIDTH_PERCENTAGE = 100;
// Guaranteed drag range at narrow viewports where the 288px pixel floor
// would otherwise meet or exceed the 55% cap, leaving no room to resize.
const DESKTOP_MIN_DRAG_RANGE_PERCENTAGE = 10;
/**
 * Calculates responsive size constraints for the right panel based on viewport width.
 *
 * Mobile (< 600px): Returns 100% width
 * Desktop: Converts fixed pixel widths to percentages of available space
 *   - Min: 288px → percentage of (viewport width - 200px sidebar), clamped
 *     so it never collides with `maxSize` (keeps the handle usable on narrow
 *     viewports)
 *   - Default: 396px → percentage of (viewport width - 200px sidebar), clamped at max
 *   - Max: 55% of available space
 *
 * IMPORTANT: callers must pass a non-decreasing viewport reference (e.g. a
 * high-water mark of `window.innerWidth`), not a live value. Recomputing
 * these props with a viewport that *shrinks* causes `react-resizable-panels`
 * to re-enforce a rising `minSize`, snapping the panel wider — see
 * POR-19964.
 *
 * @param viewPortWidth - Viewport width in pixels (must be non-decreasing across renders)
 * @returns Panel size configuration object with minSize, maxSize, and defaultSize in percentages
 */
export const getRightPanelSizeProps = (
  viewPortWidth: number,
  isMobile: boolean,
) => {
  if (isMobile) {
    return { minSize: MOBILE_MAX_WIDTH_PERCENTAGE };
  }

  // if viewPortWidth is not available, return default sizes
  if (!viewPortWidth) {
    return { minSize: 20, maxSize: 55, defaultSize: 30 };
  }

  const availableWidth = viewPortWidth - DRAWER_WIDTH;
  const minWidthInPercentage = (MIN_WIDTH_IN_PIXELS / availableWidth) * 100;
  const defaultWidthInPercentage =
    (DEFAULT_WIDTH_IN_PIXELS / availableWidth) * 100;
  // Clamp so min/default can't collide with max. Otherwise, narrow desktop
  // viewports produce `min > max`, which the library resolves by locking the
  // handle.
  const minSizeUpperBound =
    DESKTOP_MAX_WIDTH_PERCENTAGE - DESKTOP_MIN_DRAG_RANGE_PERCENTAGE;
  return {
    minSize: Math.min(minWidthInPercentage, minSizeUpperBound),
    maxSize: DESKTOP_MAX_WIDTH_PERCENTAGE,
    defaultSize: Math.min(
      defaultWidthInPercentage,
      DESKTOP_MAX_WIDTH_PERCENTAGE,
    ),
  };
};

export const ResizablePanel = ({
  children,
  className,
  ...props
}: { children: React.ReactNode; className?: string } & PanelProps) => {
  return (
    <Panel {...props} className={className}>
      {children}
    </Panel>
  );
};
